import { useCallback, useState } from 'react';

import { useSearchParams } from './use-search-params.hook';

type ModalStateProps<TSuccess = unknown, Terror = unknown, TOpen = unknown> = {
  initialValue?: boolean;
  onClose?: () => void;
  onDismiss?: (T?: Terror) => void;
  onOpen?: (T?: TOpen) => void;
  onSuccess?: (T?: TSuccess) => void;
  paramKey?: string;
};

export const useModalState = <TSuccess = unknown, Terror = unknown, TOpen = unknown>(
  arg1: ModalStateProps<TSuccess, Terror, TOpen> | string = '',
  arg2: Except<ModalStateProps<TSuccess, Terror, TOpen>, 'paramKey'> = {},
) => {
  const paramKey = typeof arg1 === 'string' ? arg1 : arg1.paramKey || '';
  const {
    initialValue = false,
    onClose,
    onDismiss,
    onOpen,
    onSuccess,
  } = typeof arg1 === 'object' ? arg1 : arg2;

  const { deleteValue, value, setValue } = useSearchParams(paramKey);

  const [isOpen, setOpen] = useState(initialValue);
  const [modalData, setModalData] = useState<TOpen | undefined>(undefined);

  const handleCloseModal = useCallback(() => {
    onClose?.();
    setOpen(false);
    setModalData(undefined);
    deleteValue();
  }, [deleteValue, onClose]);

  const handleOpenModal = useCallback(
    (data?: TOpen) => {
      onOpen?.(data);
      setModalData(data);
      setOpen(true);
    },
    [onOpen],
  );

  const handleSuccessModal = useCallback(
    (data?: TSuccess) => {
      onSuccess?.(data);
      setOpen(false);
      deleteValue();
    },
    [deleteValue, onSuccess],
  );
  const handleDismissModal = useCallback(
    (data?: Terror) => {
      onDismiss?.(data);
      setOpen(false);
      deleteValue();
    },
    [deleteValue, onDismiss],
  );

  const toggler = useCallback(
    (state: boolean) => (state ? handleOpenModal() : handleCloseModal()),
    [handleCloseModal, handleOpenModal],
  );

  return {
    isOpen: !!value || isOpen,
    toggler,
    handleCloseModal,
    handleOpenModal,
    handleSuccessModal,
    handleDismissModal,
    modalData,
    setParamValue: setValue,
    paramsValue: value || '',
  };
};
